import { useEffect } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";

interface CreateCaseModalProps {
    isOpen: boolean;
    patientName: string;
    setPatientName: (value: string) => void;
    /** Admin-side users may pick a client by lab token; blank uses the logged-in user */
    askLabToken?: boolean;
    labToken?: string;
    setLabToken?: (value: string) => void;
    error?: string | null;
    onCreate: () => void;
    onClose: () => void;
    loading?: boolean;
}

export default function CreateCaseModal({
    isOpen,
    patientName,
    setPatientName,
    askLabToken = false,
    labToken = "",
    setLabToken,
    error = null,
    onCreate,
    onClose,
    loading = false,
}: CreateCaseModalProps) {
    useEffect(() => {
        if (!isOpen) return;

        const scrollY = window.scrollY;
        const previousOverflow = document.body.style.overflow;
        const previousPosition = document.body.style.position;
        const previousTop = document.body.style.top;
        const previousWidth = document.body.style.width;

        // Lock page scroll, but keep z-index below navbar/sidebar so nav stays usable
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";

        return () => {
            document.body.style.overflow = previousOverflow;
            document.body.style.position = previousPosition;
            document.body.style.top = previousTop;
            document.body.style.width = previousWidth;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-case-title"
        >
            <div className="relative w-full max-w-lg rounded-t-2xl sm:rounded-3xl bg-[#222] border border-white/10 p-5 sm:p-6 shadow-2xl pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:pb-6">
                <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/20 sm:hidden" aria-hidden />

                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-xl bg-[#303030] text-gray-400 hover:text-white hover:bg-[#3a3a3a] transition-colors"
                >
                    <FiX className="w-5 h-5" />
                </button>

                <h2
                    id="create-case-title"
                    className="text-xl sm:text-2xl font-bold text-white mb-2 pr-10"
                >
                    Create New Case
                </h2>

                <p className="text-gray-400 text-sm mb-5 sm:mb-6 leading-relaxed">
                    {askLabToken
                        ? "Enter the patient name. Lab token is optional — if you leave it blank, the smile case is created for you."
                        : "Create a patient case before generating smile designs."}
                </p>

                <div className="space-y-4">
                    {askLabToken && (
                        <div>
                            <label className="block mb-2 text-sm text-gray-300">
                                Lab Token{" "}
                                <span className="text-gray-500 font-normal">
                                    (optional)
                                </span>
                            </label>
                            <input
                                type="text"
                                value={labToken}
                                onChange={(e) =>
                                    setLabToken?.(
                                        e.target.value
                                            .replace(/[^A-Za-z]/g, "")
                                            .toUpperCase()
                                            .slice(0, 2)
                                    )
                                }
                                className="w-full rounded-xl bg-[#303030] border border-white/10 px-4 py-3.5 outline-none text-white text-base uppercase focus:border-[#2fa9bf]/40"
                                placeholder="BL"
                                autoFocus
                                autoCapitalize="characters"
                                spellCheck={false}
                            />
                            <p className="mt-1.5 text-xs text-gray-500">
                                Two letters, no spaces. For example BL, DF, DS.
                                Leave blank to create the case for your account.
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block mb-2 text-sm text-gray-300">
                            Patient Name
                        </label>

                        <input
                            type="text"
                            value={patientName}
                            onChange={(e) => setPatientName(e.target.value)}
                            className="w-full rounded-xl bg-[#303030] border border-white/10 px-4 py-3.5 outline-none text-white text-base focus:border-[#2fa9bf]/40"
                            placeholder="Enter patient name"
                            autoFocus={!askLabToken}
                        />
                    </div>

                    {error && (
                        <p className="text-sm text-red-300">{error}</p>
                    )}

                    <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full sm:flex-1 py-3.5 rounded-xl bg-[#303030] border border-white/10 font-semibold text-white hover:bg-[#3a3a3a] transition-colors active:scale-[0.99]"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onCreate}
                            disabled={
                                !patientName.trim() ||
                                loading ||
                                (askLabToken &&
                                    labToken.trim().length === 1)
                            }
                            className="w-full sm:flex-1 py-3.5 rounded-xl bg-[#2fa9bf] font-semibold text-white disabled:opacity-50 active:scale-[0.99]"
                        >
                            {loading ? "Creating..." : "Create Case"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
